/**
 * DealCardOut -> what the board renders.
 *
 * Bound by the rule in ./README.md. Read the assertions in dealCard.test.ts
 * before adding a field: every output must trace to an input.
 *
 * Specifically NOT done here, each one a defect the previous frontend shipped:
 *   - no priority is calculated;
 *   - no recommendation is derived from priority when one is missing;
 *   - no signal is lit because a decision maker was confirmed;
 *   - nothing is aggregated.
 */
import type { DealCard, Priority, Recommendation, RiskKey, SignalKey, Stage } from '../types'
import { PROGRESSION_SIGNALS, QUALIFICATION_SIGNALS } from '../../features/pipeline/labels'

export interface BoardDeal {
  id: string
  companyName: string
  country: string | null
  archived: boolean
  /** null when the deal has not been analysed yet — never a guess. */
  stage: Stage | null
  priority: Priority | null
  recommendation: Recommendation | null
  reEvaluationRequired: boolean
  /** The user's recorded decision, or null when none has been made. */
  lastDecision: Recommendation | null
  confirmedQualification: SignalKey[]
  missingQualification: SignalKey[]
  confirmedProgression: SignalKey[]
  activeRisks: RiskKey[]
  hasBlockingFacts: boolean
}

export function toBoardDeal(card: DealCard): BoardDeal {
  const state = card.deal_current_state ?? null
  const signals = state?.confirmed_signals ?? null
  const risks = state?.active_risks ?? null

  return {
    id: card.id,
    companyName: card.company_name,
    country: card.country ?? null,
    archived: card.archived,
    stage: state?.stage ?? null,
    priority: state?.priority ?? null,
    recommendation: state?.recommendation ?? null,
    reEvaluationRequired: state?.re_evaluation_required ?? false,
    lastDecision: state?.latest_user_decision?.decision ?? null,
    confirmedQualification: signals ? QUALIFICATION_SIGNALS.filter((k) => signals[k]) : [],
    missingQualification: signals ? QUALIFICATION_SIGNALS.filter((k) => !signals[k]) : [],
    confirmedProgression: signals ? PROGRESSION_SIGNALS.filter((k) => signals[k]) : [],
    activeRisks: risks ? (Object.keys(risks) as RiskKey[]).filter((k) => risks[k]) : [],
    hasBlockingFacts: (state?.blocking_facts?.length ?? 0) > 0,
  }
}
